import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Message } from '../types';
import { format } from 'date-fns/format';
import { zhCN } from 'date-fns/locale/zh-CN';
import { User, Bot, ExternalLink, Check, Copy } from 'lucide-react';
import clsx from 'clsx';
import { useLanguage } from '../i18n/LanguageContext';
import { AI_RESPONSE_DISCLAIMER, stripAiDisclaimer } from '../utils/aiDisclaimer';

interface ChatMessageProps {
  message: Message;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = React.useState(false);
  const isUser = message.role === 'user';
  const displayContent = isUser ? message.content : stripAiDisclaimer(message.content);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={clsx(
        'flex items-start gap-3 animate-slide-up',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      <div
        className={clsx(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
          isUser ? 'bg-primary-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-slate-700'
        )}
      >
        {isUser ? (
          <User className="w-5 h-5 text-white" />
        ) : (
          <Bot className="w-5 h-5 text-gray-600 dark:text-gray-100" />
        )}
      </div>

      <div className={clsx('flex-1 max-w-[80%]', isUser ? 'items-end' : 'items-start')}>
        <div
          className={clsx(
            'group relative',
            isUser ? 'message-bubble-user' : 'message-bubble-assistant'
          )}
        >
          <div className={clsx('prose prose-sm max-w-none dark:prose-invert', isUser && 'prose-invert')}>
            <ReactMarkdown
              components={{
                a: ({ node, ...props }) => (
                  <a
                    {...props}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline inline-flex items-center gap-1 dark:text-indigo-300"
                  >
                    {props.children}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ),
              }}
            >
              {displayContent}
            </ReactMarkdown>
          </div>

          {!isUser && (
            <p className="mt-3 border-t border-gray-200/80 pt-2 text-[11px] leading-relaxed text-gray-500 dark:border-slate-600/70 dark:text-slate-300">
              {AI_RESPONSE_DISCLAIMER}
            </p>
          )}

          <button
            onClick={handleCopy}
            className="absolute -right-10 top-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded dark:hover:bg-slate-700"
            aria-label={t('复制消息', 'Copy message')}
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
            ) : (
              <Copy className="w-4 h-4 text-gray-400 dark:text-gray-200" />
            )}
          </button>
        </div>

        {message.sources && message.sources.length > 0 && !isUser && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.sources.map((source, index) => (
              <span key={index} className="source-badge">
                {t('来源', 'Source')} {source}
              </span>
            ))}
          </div>
        )}

        <div className="mt-1 text-xs text-gray-400 dark:text-gray-300">
          {format(message.timestamp, 'HH:mm', { locale: zhCN })}
        </div>
      </div>
    </div>
  );
};
